import React, { useState, useEffect, useRef, useContext } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { Link } from 'react-router-dom';
import axios from 'axios';

import 'swiper/css';
import 'swiper/css/navigation';

import './BrandSection.css';
import { ConfigContext } from '../Context/ConfigContext';
import Skeleton from './Skeleton';

const BrandSection = () => {
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const swiperRef = useRef(null);
  const { ApiUrl, apiHeaderJson, createSlug } = useContext(ConfigContext);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true);
        const response = await axios.get(`${ApiUrl}apis/CategoryList`, {
          headers: apiHeaderJson,
        });
        const { success, data } = response.data;
        if (success && Array.isArray(data)) {
          const mapped = data.map((item) => ({
            id: item.category_id,
            label: item.category_title,
          }));
          setCategories([{ id: null, label: "All Categories" }, ...mapped]);
        } else {
          setCategories([]);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      } finally {
        setCategoryLoading(false);
      }
    };
    fetchCategories();
  }, [ApiUrl, apiHeaderJson]);

  // Fetch products when activeCategory changes
  useEffect(() => {

    const fetchProducts = async () => {
      setProductsLoading(true);
      try {
        const response = await axios.get(
          `${ApiUrl}apis/GetProducts`,
          { headers: apiHeaderJson, params: { category_id: activeCategory } }
        );
        const { success, data } = response.data;
        if (success && Array.isArray(data)) {
          // Map API fields to component expected fields
          const mapped = data.map((item) => ({
            id: item.product_id,
            name: item.product_title,
            slug: item.slug || `product-${item.product_id}`,
            image: item.product_image_url,
          }));
          setProducts(mapped);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, [activeCategory, ApiUrl, apiHeaderJson]);

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
  };

  // Loading state for categories
  if (categoryLoading) {
    return (
      <section className="brand-section" aria-busy="true">
        <div className="brand-section-content">
          <div className="brand-header">
            <Skeleton type="button" width={220} height={36} style={{ marginBottom: 20 }} />
            <Skeleton type="title" width={280} height={52} />
          </div>

          <div className="brand-filter">
            {Array.from({ length: 6 }).map((_, idx) => (
              <Skeleton key={idx} type="button" width={106 + idx * 12} height={44} />
            ))}
          </div>

          <div className="skeleton-brand-grid">
            {Array.from({ length: 8 }).map((_, idx) => (
              <Skeleton
                key={idx}
                className="skeleton-brand-card"
                type="card"
                width="100%"
                height="auto"
                style={{ aspectRatio: '1 / 1' }}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="brand-section">
      <div className="brand-section-content">
        <div className="brand-header" data-aos="fade-up">
          <p className="brand-sub-heading">EXCITING GOODNESS IN EVERY BITE</p>
          <h2 className="brand-title">CATEGORIES</h2>
        </div>

        <div className="brand-filter" data-aos="fade-up" data-aos-delay="150">
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`brand-category ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => handleCategoryChange(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="brand-carousel-wrapper">
          {productsLoading ? (
            <div className="skeleton-brand-grid" aria-busy="true">
              {Array.from({ length: 8 }).map((_, idx) => (
                <Skeleton
                  key={idx}
                  className="skeleton-brand-card"
                  type="card"
                  width="100%"
                  height="auto"
                  style={{ aspectRatio: '1 / 1' }}
                />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <p>No products in this category.</p>
            </div>
          ) : (
            <>
              <Swiper
                ref={swiperRef}
                modules={[Navigation]}
                navigation={{
                  nextEl: '.swiper-button-next-custom',
                  prevEl: '.swiper-button-prev-custom',
                }}
                spaceBetween={24}
                slidesPerView={1}
                breakpoints={{
                  480: { slidesPerView: 2 },
                  768: { slidesPerView: 3 },
                  1024: { slidesPerView: 4 },
                }}
                className="brand-swiper"
              >
                {products.map((product, index) => (
                  <SwiperSlide key={product.id}>
                    <div
                      className="brand-card"
                      data-aos="flip-up"
                      data-aos-delay={(index % 4) * 100}
                    >
                      <Link to={`/product/${createSlug(product.name)}-${product.id}`} className="brand-card-link">
                        <div className="brand-card-image">
                          <img
                            src={product.image}
                            alt={`Royal Bakery ${product.name}`}
                            className="brand-logo"
                            loading="lazy"
                          />
                        </div>
                      </Link>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              <button
                className="swiper-button-prev-custom brand-arrow brand-arrow-prev"
                aria-label="Previous slide"
              >
                ❮
              </button>
              <button
                className="swiper-button-next-custom brand-arrow brand-arrow-next"
                aria-label="Next slide"
              >
                ❯
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default BrandSection;